import {
  AddClosedDimensionQuestionPayload,
  AddEffectDimensionQuestionPayload,
  CreateClosedQuestionPayload,
  CreateEffectQuestionPayload,
  ExtendClosedQuestionPayload,
  ExtendEffectQuestionPayload,
  i18nQuestion,
} from 'features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import { DemographicsPayload, Perspectives } from 'features/lead/lead-questionnaire/lead-questionnaire.types';
import _ from 'lodash';
import Logger from '../logger';
import { API_V1 } from './APIs/APIv1';
import { AxiosResponse } from 'axios';

export const getAllLeadTags = async () => {
  try {
    const response = await API_V1.get('/lead/tags');
    return {
      data: response.data,
    };
  } catch (e) {
    Logger.trace('Error while fetching all lead tags.');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getAllLeadTagsByFolderId = async (folderId: string): Promise<AxiosResponse<any, any>['data']> => {
  try {
    const response = await API_V1.get(`/lead/tag-folders/${folderId}`);
    return {
      data: {
        name: response.data.name,
        leadTags: response.data.content.leadTags,
        total: response.data.content.total,
        subFolders: response.data.subFolders,
      },
    };
  } catch (e) {
    Logger.trace('Error while fetching all lead tags from the selected folder.');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const createLeadTag = async (
  name: string,
  closedQuestions: string[],
  openQuestions: string[],
  effectQuestions: string[],
  participants: Array<{ name: string; perspective: string }>,
  folder: string
) => {
  try {
    const response = await API_V1.post('/lead/tags', {
      name,
      closedQuestions,
      openQuestions,
      effectQuestions,
      participants,
      folder,
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while creating a new lead tag');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const deleteLeadTag = async (leadTagId: string) => {
  try {
    const response = await API_V1.delete(`/lead/tags/${leadTagId}`);
    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting a lead tag');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const editLeadTag = async (leadTagId: string, name: string) => {
  try {
    const response = await API_V1.put(`/lead/tags/${leadTagId}`, { name });
    return response.data;
  } catch (e) {
    Logger.trace('Error while editing a lead tag');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const moveLeadTag = async (leadTagId: string, newFolder: string) => {
  try {
    const response = await API_V1.put(`/lead/tags/${leadTagId}`, { folder: newFolder });
    return response.data;
  } catch (e) {
    Logger.trace('Error while moving a lead tag');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getLeadTagDetails = async (id: string) => {
  try {
    const response = await API_V1.get(`/lead/tags/${id}`);
    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching lead tag details by tag id.');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getAllClosedLeadQuestions = async () => {
  try {
    const response = await API_V1.get('/lead/closed-questions');
    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching all closed lead questions.');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const createClosedLeadQuestion = async (closedQuestion: CreateClosedQuestionPayload[]) => {
  try {
    const response = await API_V1.post('/lead/closed-questions', { newClosedQuestions: closedQuestion });
    return response.data;
  } catch (e) {
    Logger.trace('Error while creating a new category.');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const createEffectLeadQuestions = async (effectQuestions: CreateEffectQuestionPayload[]) => {
  try {
    const response = await API_V1.post('/lead/effect-questions', { newEffectQuestions: effectQuestions });
    return response.data;
  } catch (e) {
    Logger.trace('Error while creating new effect questions.');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const createOpenLeadQuestions = async (openQuestions: i18nQuestion[]) => {
  try {
    const response = await API_V1.post('/lead/open-questions', { newOpenQuestions: openQuestions });
    return response.data;
  } catch (e) {
    Logger.trace('Error while creating new open questions.');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getAllOpenLeadQuestions = async () => {
  try {
    const response = await API_V1.get('/lead/open-questions');
    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching all open lead questions.');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getAllEffectLeadQuestions = async () => {
  try {
    const response = await API_V1.get('/lead/effect-questions');
    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching all effect lead questions.');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const addParticipantToLeadTag = async (id: string, participants: { name: string; perspective: string }[]) => {
  try {
    const response = await API_V1.post(`/lead/tags/${id}/participant`, { participants });
    return response.data;
  } catch (e) {
    Logger.trace(`Error while trying to add participants to lead tag ${id}`);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const updateParticipantToLeadTag = async (
  id: string,
  participantId: string,
  participant: { isSuspended?: boolean; perspective?: string }
) => {
  try {
    const response = await API_V1.put(`/lead/tags/${id}/participant/${participantId}`, { ...participant });
    return response.data;
  } catch (e) {
    Logger.trace(`Error while trying to edit participants to lead tag ${id}`);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const deleteLeadQuestion = async (questionId: string) => {
  try {
    const response = await API_V1.delete(`/lead/questions/${questionId}`);
    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting lead question!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const deleteLeadCategory = async (categoryId: string) => {
  try {
    const response = await API_V1.delete(`/lead/categories/${categoryId}`);
    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting lead category!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

// Public endpoint - no auth required
export const getQuestionnaire = async (tagId: string, participantId: string, tenantId: string, lang: string) => {
  try {
    const response = await API_V1.get(`/lead/tags/${tagId}/participant/${participantId}?tenantId=${tenantId}&lang=${lang}`);
    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching participant questionnaire');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

// Public endpoint - no auth required
export const sendAnswerLeadQuestionnaire = async (
  tagId: string,
  participantId: string,
  tenantId: string,
  closedQuestions: Array<{ question: string; answer: number }>,
  effectQuestions: Array<{ question: string; answer: number }>,
  openQuestions: Array<{ question: string; answer: string[] }>,
  demographics: DemographicsPayload
) => {
  const demographicsFitltered = _.mapValues(demographics, (e) => (_.isEmpty(e) ? undefined : e));
  try {
    const res = await API_V1.post(
      `/lead/tags/${tagId}/participant/${participantId}?tenantId=${tenantId}`,
      {
        answers: {
          closedQuestions,
          effectQuestions,
          openQuestions,
        },
        demographics: demographicsFitltered,
      },
      {}
    );
    return res.data;
  } catch (e) {
    Logger.trace('Error while sending questionnaire answers}');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

// Public endpoint - no auth required
export const getAnonymousLeadTagDetails = async (id: string, tenantId: string, lang: string) => {
  try {
    const response = await API_V1.get(`/lead/tags/${id}/participant-completed?tenantId=${tenantId}&lang=${lang}`);
    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching anonymous lead tag details by tag id.');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

// Public endpoint - no auth required
export const sendAnonymousAnswerLeadQuestionnaire = async (
  tagId: string,
  perspective: Perspectives,
  tenantId: string,
  closedQuestions: Array<{ question: string; answer: number }>,
  effectQuestions: Array<{ question: string; answer: number }>,
  openQuestions: Array<{ question: string; answer: string[] }>,
  demographics: DemographicsPayload
) => {
  const demographicsFitltered = _.mapValues(demographics, (e) => (_.isEmpty(e) ? undefined : e));
  try {
    const res = await API_V1.post(
      `/lead/tags/${tagId}/participant-completed?tenantId=${tenantId}`,
      {
        perspective,
        answers: {
          closedQuestions,
          effectQuestions,
          openQuestions,
        },
        demographics: demographicsFitltered,
      },
      {}
    );
    return res.data;
  } catch (e) {
    Logger.trace('Error while sending questionnaire answers}');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const addLeadFolder = async (name: string) => {
  try {
    const response = await API_V1.post('/lead/tag-folders', { name });
    return response.data;
  } catch (e) {
    Logger.trace('Error while trying to add new lead tag folder');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getAllLeadFolders = async () => {
  try {
    const response = await API_V1.get('/lead/tag-folders');
    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching lead tag folders');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const addLeadSubFolder = async (folderId: string, name: string) => {
  try {
    const response = await API_V1.post(`/lead/tag-folders/${folderId}`, { name });
    return response.data;
  } catch (e) {
    Logger.trace('Error while adding a lead tag subfolder');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const getLeadReport = async (leadTagId: string, lang?: string) => {
  try {
    const response = await API_V1.get(`/lead/reports/${leadTagId}`, {
      headers: {
        Accept: 'application/pdf',
      },
      params: {
        language: lang,
      },
      responseType: 'blob',
    });
    return response.data;
  } catch (e) {
    Logger.trace('Error while getting lead report for %s', leadTagId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const addClosedCategoryDimension = async (payload: ExtendClosedQuestionPayload[]) => {
  try {
    const response = await API_V1.post('/lead/closed-questions', { newClosedQuestions: payload });
    return response.data;
  } catch (e) {
    Logger.trace('Error while adding new dimension to lead category');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const addEffectCategoryDimension = async (payload: ExtendEffectQuestionPayload[]) => {
  try {
    const response = await API_V1.post('/lead/effect-questions', { newEffectQuestions: payload });
    return response.data;
  } catch (e) {
    Logger.trace('Error while adding new dimension to lead category');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const addClosedDimensionQuestions = async (payload: AddClosedDimensionQuestionPayload[]) => {
  try {
    const response = await API_V1.post('/lead/closed-questions', { newClosedQuestions: payload });
    return response.data;
  } catch (e) {
    Logger.trace('Error while adding new question to lead dimension');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};

export const addEffectDimensionQuestions = async (payload: AddEffectDimensionQuestionPayload[]) => {
  try {
    const response = await API_V1.post('/lead/effect-questions', { newEffectQuestions: payload });
    return response.data;
  } catch (e) {
    Logger.trace('Error while adding new question to lead dimension');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e) };
  }
};
